import { useEffect, useRef } from "react";

/**
 * useVisitorIntent — passive, zero-UI hook that watches how each visitor
 * moves through the landing page and builds an intent profile Sable can
 * personalize against.
 *
 * What it tracks:
 *   sections_viewed: {[sectionId]: seconds spent visible ≥ 40% in viewport}
 *   clicks:          list of {testid, ts} for every element with data-testid clicked
 *   video_played:    bool — did they hit play on the Spotlight video?
 *   quiz_started:    bool — did they open the Package Quiz?
 *   package_hovered: array of package keys they lingered on
 *   page_time_s:     total seconds on page
 *   entry_referrer:  document.referrer at first-load
 *   last_seen:       ISO timestamp
 *
 * Storage: localStorage under `sb.visitor.intent` (single JSON blob). Reset
 * on hard-refresh only after 24h to keep the profile fresh but persistent
 * across scroll / SPA nav.
 */
const STORAGE_KEY = "sb.visitor.intent";
const MAX_CLICKS = 40;
const STALE_MS = 24 * 60 * 60 * 1000; // 24h

function _read() {
  if (typeof window === "undefined") return _empty();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return _empty();
    const parsed = JSON.parse(raw);
    if (parsed?.first_seen && Date.now() - new Date(parsed.first_seen).getTime() > STALE_MS) {
      return _empty();
    }
    return { ..._empty(), ...parsed };
  } catch {
    return _empty();
  }
}

function _empty() {
  return {
    first_seen: new Date().toISOString(),
    last_seen: new Date().toISOString(),
    entry_referrer: typeof document !== "undefined" ? document.referrer || "" : "",
    sections_viewed: {},
    clicks: [],
    video_played: false,
    quiz_started: false,
    package_hovered: [],
    page_time_s: 0,
  };
}

function _write(profile) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    /* localStorage full or blocked — silently ignore, profile stays in-mem */
  }
}

/** Read the current intent profile from localStorage. Cheap; call any time. */
export function readVisitorIntent() {
  return _read();
}

/**
 * Derive a compact `visitor_context` payload the chat API can consume.
 * Extracts the top-3 most-viewed sections and the most-clicked testids so
 * Sable's context stays under a few hundred tokens.
 */
export function summarizeVisitorIntent() {
  const p = _read();
  const now = Date.now();
  const first = new Date(p.first_seen).getTime();
  const pageTime = Math.max(p.page_time_s, Math.round((now - first) / 1000));

  const topSections = Object.entries(p.sections_viewed || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([id, secs]) => ({ id, seconds: Math.round(secs) }));

  const clickCounts = {};
  for (const c of p.clicks || []) clickCounts[c.testid] = (clickCounts[c.testid] || 0) + 1;
  const topClicks = Object.entries(clickCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([testid, count]) => ({ testid, count }));

  return {
    page_time_s: pageTime,
    entry_referrer: p.entry_referrer || "",
    top_sections: topSections,
    top_clicks: topClicks,
    video_played: !!p.video_played,
    quiz_started: !!p.quiz_started,
    package_hovered: (p.package_hovered || []).slice(-5),
    is_returning: pageTime > 30 || (p.clicks || []).length > 5,
  };
}

export default function useVisitorIntent() {
  const profileRef = useRef(_read());
  const timersRef = useRef({});      // sectionId → start-ms when in view
  const tickRef = useRef(null);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const profile = profileRef.current;

    // 1. IntersectionObserver — track cumulative seconds each <section id=...>
    //    is at least 40% visible.
    const sections = Array.from(document.querySelectorAll("section[id]"));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = e.target.id;
          if (!id) continue;
          if (e.isIntersecting && e.intersectionRatio >= 0.4) {
            timersRef.current[id] = Date.now();
          } else if (timersRef.current[id]) {
            const dur = (Date.now() - timersRef.current[id]) / 1000;
            profile.sections_viewed[id] = (profile.sections_viewed[id] || 0) + dur;
            delete timersRef.current[id];
          }
        }
      },
      { threshold: [0.4] },
    );
    sections.forEach((s) => io.observe(s));

    // 2. Click listener — capture every data-testid the visitor taps. Cheap.
    const onClick = (ev) => {
      const el = ev.target.closest?.("[data-testid]");
      if (!el) return;
      const testid = el.getAttribute("data-testid");
      if (!testid) return;
      profile.clicks.push({ testid, ts: new Date().toISOString() });
      if (profile.clicks.length > MAX_CLICKS) {
        profile.clicks = profile.clicks.slice(-MAX_CLICKS);
      }
      // Flags for common intents
      if (testid.includes("quiz") || testid.includes("package-quiz")) {
        profile.quiz_started = true;
      }
      if (testid.includes("spotlight-youtube") || testid.includes("hero-video")) {
        profile.video_played = true;
      }
      const pkgMatch = testid.match(/tier-cta-(\w+)|book-(\w+)/);
      if (pkgMatch) {
        const key = pkgMatch[1] || pkgMatch[2];
        if (key && !profile.package_hovered.includes(key)) {
          profile.package_hovered.push(key);
          if (profile.package_hovered.length > 8) {
            profile.package_hovered = profile.package_hovered.slice(-8);
          }
        }
      }
    };
    document.addEventListener("click", onClick, { capture: true, passive: true });

    // 3. Persist every 8s (batching keeps localStorage writes cheap).
    tickRef.current = setInterval(() => {
      // Flush any still-in-view timers so page-time stays accurate
      const now = Date.now();
      for (const [id, start] of Object.entries(timersRef.current)) {
        const dur = (now - start) / 1000;
        profile.sections_viewed[id] = (profile.sections_viewed[id] || 0) + dur;
        timersRef.current[id] = now;
      }
      profile.page_time_s = Math.round((now - startedAtRef.current) / 1000) + (profile.page_time_s || 0);
      startedAtRef.current = now;
      profile.last_seen = new Date().toISOString();
      _write(profile);
    }, 8000);

    // 4. Flush on tab hide (best signal for real page-time)
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        for (const [id, start] of Object.entries(timersRef.current)) {
          const dur = (Date.now() - start) / 1000;
          profile.sections_viewed[id] = (profile.sections_viewed[id] || 0) + dur;
        }
        timersRef.current = {};
        _write(profile);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      io.disconnect();
      document.removeEventListener("click", onClick, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
      if (tickRef.current) clearInterval(tickRef.current);
      // Final flush
      _write(profile);
    };
  }, []);
}
